import type { ApprovalRequest } from '@/lib/types';
import { formatDate } from '../format';
import { BucketShell } from './_BucketShell';
import {
  readDefaultFields,
  readProcessedBy,
  readResolvedAt,
  readStructuredFields,
} from './_payload';
import type { DefaultFields } from '../types';

export interface DuplicateViewProps {
  req: ApprovalRequest;
}

interface DuplicateOf {
  ref_no?: string | null;
  purchase_invoice_id?: string | null;
  posted_date?: string | null;
}

function asDuplicateOf(value: unknown): DuplicateOf {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as DuplicateOf;
}

// Slice 4 Task 4.4: per-row fallback specifically for DUPLICATE rows. N8N
// short-circuits these before persisting full default_fields (Slice 5 will
// finalise the intake), so we walk default_fields -> gemini_raw -> null
// just for this view rather than letting the global _payload fallback bleed
// gemini_raw into every other bucket.
function fallback(
  field: keyof DefaultFields,
  def: DefaultFields,
  raw: Record<string, unknown> | null,
): string | null {
  const v = def[field];
  if (v !== null && v !== undefined && v !== '') return String(v);
  if (raw && typeof raw === 'object') {
    const candidate = (raw as Record<string, unknown>)[field as string];
    if (candidate !== null && candidate !== undefined && candidate !== '') {
      return String(candidate);
    }
  }
  return null;
}

export function DuplicateView({ req }: DuplicateViewProps) {
  const sf = readStructuredFields(req);
  const fields = readDefaultFields(req);
  const raw = fields.gemini_raw ?? null;
  const duplicateOf = asDuplicateOf(sf['duplicate_of']);
  const reason =
    typeof sf['duplicate_reason'] === 'string'
      ? (sf['duplicate_reason'] as string)
      : null;
  const refNo = duplicateOf.ref_no ?? null;
  const piId = duplicateOf.purchase_invoice_id ?? null;
  const postedDate = duplicateOf.posted_date ?? null;

  const vendor = fallback('vendor', fields, raw);
  const invoiceNo = fallback('invoice_no', fields, raw);
  const invoiceDate = fallback('invoice_date', fields, raw);
  const grossAmount = fallback('gross_amount', fields, raw);
  const detectedAt = formatDate(readResolvedAt(req));
  const processedBy = readProcessedBy(req);

  return (
    <BucketShell req={req}>
      <section className="rounded-lg border border-warning-500/40 bg-warning-500/10 p-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-warning-700">
          Duplicate invoice
        </p>
        <p className="mt-8 text-base font-semibold text-warning-700">
          {refNo ? `Duplicate of PI #${refNo}` : 'Duplicate of an earlier purchase invoice'}
          {postedDate ? ` (posted ${formatDate(postedDate)})` : ''}
        </p>
        <dl className="mt-12 grid gap-8 text-sm text-neutral-800 tablet:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Original PI ref
            </dt>
            <dd className="mt-2 font-mono">{refNo ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Original PI ID
            </dt>
            <dd className="mt-2 font-mono">{piId ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Reason
            </dt>
            <dd className="mt-2">{reason ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Vendor
            </dt>
            <dd className="mt-2">{vendor ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Invoice no
            </dt>
            <dd className="mt-2 font-mono">{invoiceNo ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Invoice date / amount
            </dt>
            <dd className="mt-2">
              {invoiceDate ? formatDate(invoiceDate) : '—'}
              {grossAmount ? ` · ${grossAmount}` : ''}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Detected at
            </dt>
            <dd className="mt-2">{detectedAt}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Processed by
            </dt>
            <dd className="mt-2 truncate" title={processedBy}>
              {processedBy}
            </dd>
          </div>
        </dl>
      </section>
    </BucketShell>
  );
}
